import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  getAssets,
  addAdminCurrency,
  updateAsset,
  deleteAdminCurrency,
  syncAssetPrices,
} from "../../../api/apiServices";

// ── Helpers ────────────────────────────────────────────────────
const fmtNGN = (n) => `₦${Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;

const NETWORK_COLORS = {
  bsc:    { bg: "#F59E0B18", color: "#F59E0B", label: "BSC"    },
  eth:    { bg: "#627EEA18", color: "#627EEA", label: "ETH"    },
  ton:    { bg: "#0088CC18", color: "#0088CC", label: "TON"    },
  sol:    { bg: "#9945FF18", color: "#9945FF", label: "SOL"    },
  tron:   { bg: "#FF060618", color: "#FF0606", label: "TRON"   },
};
const netStyle = (net) => NETWORK_COLORS[net?.toLowerCase()] || { bg: "#ffffff10", color: "#94a3b8", label: net?.toUpperCase() || "?" };

// ── Confirm Modal ──────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel = "Confirm", confirmColor = "red", onConfirm, onCancel, loading }) {
  const btnCls = {
    red:   "bg-red-600 hover:bg-red-700 text-white",
    green: "bg-[#00C896] hover:bg-[#00b386] text-black",
    amber: "bg-amber-500 hover:bg-amber-600 text-black",
  }[confirmColor] || "bg-red-600 text-white";
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-[#1a1a1a] rounded-2xl border border-white/10 w-full max-w-sm shadow-2xl">
        <div className="px-6 pt-6 pb-4 text-center space-y-2">
          <p className="text-white font-bold">{title}</p>
          <p className="text-white/40 text-sm leading-relaxed">{message}</p>
        </div>
        <div className="flex gap-2 px-6 pb-6">
          <button onClick={onCancel} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-white/5 text-white/50 text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-40">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className={`flex-1 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${btnCls}`}>
            {loading ? <><div className="w-3.5 h-3.5 border-2 border-current/30 border-t-current rounded-full animate-spin" />Working…</> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Add Currency Modal
// ─────────────────────────────────────────────────────────────
function AddCurrencyModal({ onClose, onAdded }) {
  const [form, setForm]     = useState({ symbol: "", network: "bsc", contractAddress: "", decimals: "18" });
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const NETWORKS = ["bsc", "eth", "ton", "sol", "tron"];
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleAdd = async () => {
    setError("");
    if (!form.symbol.trim()) { setError("Symbol is required"); return; }
    setLoading(true);
    try {
      const res = await addAdminCurrency({
        symbol:          form.symbol.trim().toUpperCase(),
        network:         form.network,
        contractAddress: form.contractAddress.trim(),
        decimals:        Number(form.decimals) || 18,
      });
      if (res.status) {
        toast.success(`${form.symbol.toUpperCase()} added on ${form.network.toUpperCase()}`);
        onAdded();
        onClose();
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to add currency");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="relative w-full max-w-md bg-[#161616] border border-white/8 rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <p className="text-white font-bold text-sm">Add New Currency</p>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-xs">
              <i className="ri-error-warning-line" /> {error}
            </div>
          )}

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Symbol</label>
            <input
              value={form.symbol}
              onChange={e => set("symbol", e.target.value)}
              placeholder="e.g. USDT, BTC, NOT"
              className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 uppercase"
            />
            <p className="text-white/20 text-[10px] mt-1">CoinGecko ID is resolved automatically</p>
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Network</label>
            <div className="grid grid-cols-5 gap-1.5">
              {NETWORKS.map(net => {
                const s = netStyle(net);
                return (
                  <button key={net} onClick={() => set("network", net)}
                    className={`py-2.5 rounded-xl text-[10px] font-bold uppercase border transition-all ${
                      form.network === net ? "text-white" : "bg-white/4 text-white/30 border-white/8 hover:bg-white/8"
                    }`}
                    style={form.network === net ? { background: s.bg, color: s.color, borderColor: s.color + "40" } : {}}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">
              Contract Address <span className="text-white/20 normal-case">(leave blank for native coins)</span>
            </label>
            <input
              value={form.contractAddress}
              onChange={e => set("contractAddress", e.target.value)}
              placeholder="0x… or EQ… or leave blank"
              className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 font-mono text-xs"
            />
          </div>

          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">Decimals</label>
            <input
              type="number"
              value={form.decimals}
              onChange={e => set("decimals", e.target.value)}
              className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00C896]/50"
            />
          </div>

          <button onClick={handleAdd} disabled={loading}
            className="w-full py-3 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {loading
              ? <><div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />Adding…</>
              : <><i className="ri-add-line" />Add Currency</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Edit Asset Modal
// ─────────────────────────────────────────────────────────────
function EditAssetModal({ asset, onClose, onRefresh }) {
  const [isActive, setIsActive]       = useState(asset.isActive);
  const [coingeckoId, setCoingeckoId] = useState(asset.coingeckoId || "");
  const [loading, setLoading]         = useState(false);
  const [delLoading, setDelLoading]   = useState(false);
  const [saved, setSaved]             = useState(false);
  const [confirm, setConfirm]         = useState(null);
  const ns = netStyle(asset.network);

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateAsset(asset.id, { isActive, coingeckoId: coingeckoId.trim() });
      setSaved(true);
      onRefresh();
      setTimeout(onClose, 1200);
    } catch {
      toast.error("Failed to update asset");
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    setDelLoading(true);
    try {
      await deleteAdminCurrency(asset.id);
      toast.success(`${asset.symbol} on ${asset.network.toUpperCase()} deactivated`);
      onRefresh();
      onClose();
    } catch { toast.error("Delete failed"); }
    finally { setDelLoading(false); setConfirm(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-[#161616] border border-white/8 rounded-2xl overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            {asset.image
              ? <img src={asset.image} className="w-8 h-8 rounded-full" alt="" />
              : <div className="w-8 h-8 rounded-full bg-[#00C896]/10 flex items-center justify-center text-[#00C896] text-xs font-bold">{asset.symbol.slice(0, 2)}</div>
            }
            <div>
              <p className="text-sm font-semibold text-white">{asset.symbol}</p>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: ns.bg, color: ns.color }}>
                {ns.label}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/5 flex items-center justify-center hover:bg-white/10">
            <i className="ri-close-line text-white/50" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Prices (read-only) */}
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { label: "NGN Price", value: fmtNGN(asset.priceNgn) },
              { label: "USD Price", value: fmtUSD(asset.priceUsd) },
              { label: "24h Change", value: `${Number(asset.change24h || 0).toFixed(2)}%`, color: asset.change24h >= 0 ? "#00C896" : "#f87171" },
            ].map(s => (
              <div key={s.label} className="bg-white/4 rounded-xl p-2.5 border border-white/5">
                <p className="text-[10px] text-white/30 mb-0.5">{s.label}</p>
                <p className="text-xs font-bold" style={{ color: s.color || "white" }}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* CoinGecko ID */}
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-1.5">CoinGecko ID</label>
            <input
              value={coingeckoId}
              onChange={e => setCoingeckoId(e.target.value)}
              placeholder="e.g. tether, bitcoin"
              className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/50 font-mono"
            />
            <p className="text-white/20 text-[10px] mt-1">Changing this triggers an immediate price sync</p>
          </div>

          {/* Active toggle */}
          <div>
            <label className="text-white/40 text-[10px] uppercase tracking-wide block mb-2">Status</label>
            <div className="flex gap-2">
              {[true, false].map(state => (
                <button key={String(state)} onClick={() => setIsActive(state)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all border ${
                    isActive === state
                      ? state ? "bg-[#00C896]/15 text-[#00C896] border-[#00C896]/30" : "bg-red-500/15 text-red-400 border-red-500/30"
                      : "bg-white/5 text-white/30 border-white/8 hover:bg-white/10"
                  }`}>
                  {state ? "✓ Active" : "✗ Paused"}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-white/25 mt-2 text-center">
              {isActive ? "Users can trade this asset." : "Trading is disabled for this asset."}
            </p>
          </div>

          {/* Contract address (read-only info) */}
          {asset.contractAddress && (
            <div className="bg-white/4 rounded-xl border border-white/5 px-4 py-3">
              <p className="text-white/30 text-[10px] mb-0.5">Contract Address</p>
              <p className="text-white/60 text-[10px] font-mono break-all">{asset.contractAddress}</p>
            </div>
          )}

          {/* Save */}
          {saved ? (
            <div className="py-3 rounded-xl bg-[#00C896]/10 border border-[#00C896]/20 flex items-center justify-center gap-2">
              <i className="ri-checkbox-circle-line text-[#00C896]" />
              <span className="text-sm font-semibold text-[#00C896]">Saved</span>
            </div>
          ) : (
            <button onClick={handleSave} disabled={loading}
              className="w-full py-2.5 rounded-xl bg-[#00C896] text-black text-sm font-bold hover:bg-[#00b386] transition-colors disabled:opacity-50">
              {loading ? "Saving…" : "Apply Changes"}
            </button>
          )}

          {/* Deactivate */}
          <button
            onClick={() => setConfirm({
              title:   `Deactivate ${asset.symbol}`,
              message: `Deactivate ${asset.symbol} on ${asset.network.toUpperCase()}? Users won't be able to trade it until re-enabled.`,
              onConfirm: handleDelete,
            })}
            disabled={delLoading}
            className="w-full py-2.5 rounded-xl bg-red-500/10 text-red-400 text-xs font-bold border border-red-500/20 hover:bg-red-500/20 transition-colors disabled:opacity-50">
            <i className="ri-forbid-line mr-1.5" />Deactivate Asset
          </button>
        </div>
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel="Deactivate"
          confirmColor="red"
          loading={delLoading}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main AssetsView
// ─────────────────────────────────────────────────────────────
export default function AssetsView() {
  const [assets, setAssets]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [syncing, setSyncing]     = useState(false);
  const [selected, setSelected]   = useState(null);
  const [showAdd, setShowAdd]     = useState(false);
  const [search, setSearch]       = useState("");
  const [netFilter, setNetFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "active" | "paused"

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAssets();
      setAssets(res.status ? res.data : []);
    } catch { toast.error("Failed to load assets"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAssets(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncAssetPrices();
      toast.success(res.message || "Prices synced");
      fetchAssets();
    } catch { toast.error("Price sync failed"); }
    finally { setSyncing(false); }
  };

  // ── Derived ────────────────────────────────────────────────
  const networks   = [...new Set(assets.map(a => a.network))];
  const filtered   = assets.filter(a => {
    const matchSearch  = !search.trim() || a.symbol.toLowerCase().includes(search.toLowerCase()) || (a.name || "").toLowerCase().includes(search.toLowerCase());
    const matchNet     = !netFilter  || a.network === netFilter;
    const matchStatus  = !statusFilter || a.status === statusFilter;
    return matchSearch && matchNet && matchStatus;
  });
  const activeCount  = assets.filter(a => a.isActive).length;
  const pausedCount  = assets.filter(a => !a.isActive).length;

  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-white">Assets</h2>
          <p className="text-white/40 text-xs mt-0.5">
            {assets.length} currencies · {activeCount} active · {pausedCount} paused
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleSync} disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 border border-white/8 text-xs text-white/60 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-40">
            <i className={`ri-refresh-line ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Prices"}
          </button>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#00C896] text-black text-xs font-bold hover:bg-[#00b386] transition-colors">
            <i className="ri-add-line" />Add Currency
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="flex gap-2 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search symbol or name…"
            className="w-full bg-[#161616] border border-white/8 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#00C896]/40" />
        </div>
        {/* Network filter */}
        <select value={netFilter} onChange={e => setNetFilter(e.target.value)}
          className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none">
          <option value="">All Networks</option>
          {networks.map(n => <option key={n} value={n}>{n.toUpperCase()}</option>)}
        </select>
        {/* Status filter */}
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="bg-[#161616] border border-white/8 rounded-xl px-3 py-2.5 text-xs text-white/60 focus:outline-none">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
        </select>
      </div>

      {/* ── Desktop table ── */}
      <div className="bg-[#161616] border border-white/5 rounded-xl overflow-hidden hidden md:block">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/5 bg-white/[0.02]">
              {["Asset", "Network", "NGN Price", "USD Price", "24h", "CoinGecko ID", "Status", ""].map(h => (
                <th key={h} className="px-5 py-3 text-[10px] font-bold text-white/25 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/4">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(8)].map((_, j) => (
                    <td key={j} className="px-5 py-4">
                      <div className="h-3 bg-white/5 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-5 py-14 text-center">
                  <i className="ri-coin-line text-white/10 text-4xl block mb-2" />
                  <p className="text-white/25 text-sm">No assets found</p>
                </td>
              </tr>
            ) : filtered.map(asset => {
              const ns = netStyle(asset.network);
              return (
                <tr key={asset.id} className="hover:bg-white/2 transition-colors">
                  {/* Asset */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      {asset.image
                        ? <img src={asset.image} className="w-8 h-8 rounded-full" alt="" />
                        : <div className="w-8 h-8 rounded-full bg-[#00C896]/10 flex items-center justify-center text-[#00C896] text-[10px] font-bold">{asset.symbol.slice(0, 2)}</div>
                      }
                      <div>
                        <p className="text-xs font-bold text-white">{asset.symbol}</p>
                        <p className="text-[10px] text-white/30">{asset.name || asset.symbol}</p>
                      </div>
                    </div>
                  </td>
                  {/* Network */}
                  <td className="px-5 py-3.5">
                    <span className="text-[10px] font-bold px-2 py-1 rounded-lg" style={{ background: ns.bg, color: ns.color }}>
                      {ns.label}
                    </span>
                  </td>
                  {/* NGN */}
                  <td className="px-5 py-3.5 text-xs font-semibold text-white">{fmtNGN(asset.priceNgn)}</td>
                  {/* USD */}
                  <td className="px-5 py-3.5 text-xs text-white/50">{fmtUSD(asset.priceUsd)}</td>
                  {/* 24h */}
                  <td className={`px-5 py-3.5 text-xs font-bold ${Number(asset.change24h || 0) >= 0 ? "text-[#00C896]" : "text-red-400"}`}>
                    {Number(asset.change24h || 0).toFixed(2)}%
                  </td>
                  {/* CG ID */}
                  <td className="px-5 py-3.5">
                    <span className="text-[10px] font-mono text-white/30">{asset.coingeckoId}</span>
                  </td>
                  {/* Status */}
                  <td className="px-5 py-3.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                      asset.isActive ? "bg-[#00C896]/10 text-[#00C896]" : "bg-red-500/10 text-red-400"
                    }`}>
                      {asset.isActive ? "Active" : "Paused"}
                    </span>
                  </td>
                  {/* Action */}
                  <td className="px-5 py-3.5 text-right">
                    <button onClick={() => setSelected(asset)}
                      className="text-xs font-bold text-[#00C896] hover:underline">
                      Manage
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Mobile cards ── */}
      <div className="md:hidden space-y-3">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-[#161616] rounded-xl border border-white/5 animate-pulse" />
          ))
        ) : filtered.map(asset => {
          const ns = netStyle(asset.network);
          return (
            <div key={asset.id} className="bg-[#161616] border border-white/5 rounded-xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {asset.image
                    ? <img src={asset.image} className="w-9 h-9 rounded-full" alt="" />
                    : <div className="w-9 h-9 rounded-full bg-[#00C896]/10 flex items-center justify-center text-[#00C896] text-xs font-bold">{asset.symbol.slice(0, 2)}</div>
                  }
                  <div>
                    <p className="text-sm font-bold text-white">{asset.symbol}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: ns.bg, color: ns.color }}>{ns.label}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${asset.isActive ? "bg-[#00C896]/10 text-[#00C896]" : "bg-red-500/10 text-red-400"}`}>
                        {asset.isActive ? "Active" : "Paused"}
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelected(asset)}
                  className="text-[10px] font-bold text-[#00C896] uppercase tracking-widest bg-[#00C896]/10 px-3 py-1.5 rounded-lg hover:bg-[#00C896]/20 transition-colors">
                  Manage
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "NGN",  value: fmtNGN(asset.priceNgn) },
                  { label: "USD",  value: fmtUSD(asset.priceUsd) },
                  { label: "24h",  value: `${Number(asset.change24h || 0).toFixed(2)}%`, color: Number(asset.change24h || 0) >= 0 ? "#00C896" : "#f87171" },
                ].map(s => (
                  <div key={s.label} className="bg-[#0f0f0f] rounded-lg p-2 text-center">
                    <p className="text-[9px] text-white/30 mb-0.5">{s.label}</p>
                    <p className="text-[10px] font-bold truncate" style={{ color: s.color || "white" }}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Modals ── */}
      {selected && (
        <EditAssetModal
          asset={selected}
          onClose={() => setSelected(null)}
          onRefresh={fetchAssets}
        />
      )}
      {showAdd && (
        <AddCurrencyModal
          onClose={() => setShowAdd(false)}
          onAdded={fetchAssets}
        />
      )}
    </div>
  );
}
